import { z } from "zod";

export const certificateFieldKeys = [
	"participantName",
	"projectName",
	"score",
	"maxScore",
	"percentage",
	"completedAt",
	"issuedAt",
	"examinerName",
	"certificateNumber",
	"verificationUrl",
] as const;

export type CertificateFieldKey = (typeof certificateFieldKeys)[number];
export const certificateHorizontalAlignments = ["left", "center"] as const;
export type CertificateHorizontalAlignment =
	(typeof certificateHorizontalAlignments)[number];

const elementSchema = z.object({
	id: z.string(),
	type: z.enum(["text", "field", "rect", "circle", "line", "image", "seal", "qr"]),
	x: z.number(),
	y: z.number(),
	width: z.number().positive().optional(),
	height: z.number().positive().optional(),
	rotation: z.number().default(0),
	fill: z.string().default("#172554"),
	stroke: z.string().default("#172554"),
	strokeWidth: z.number().min(0).default(1),
	fontSize: z.number().positive().default(20),
	fontFamily: z.enum(["Helvetica", "Times-Roman", "Courier"]).default("Helvetica"),
	horizontalAlignment: z
		.enum(certificateHorizontalAlignments)
		.default("left"),
	text: z.string().optional(),
	field: z.enum(certificateFieldKeys).optional(),
	assetFileId: z.string().optional(),
});

export const certificateDesignSchema = z.object({
	version: z.literal(1),
	orientation: z.enum(["portrait", "landscape"]),
	background: z.string().default("#ffffff"),
	elements: z.array(elementSchema).max(100),
});

export type CertificateElement = z.infer<typeof elementSchema>;
export type CertificateDesign = z.infer<typeof certificateDesignSchema>;

const id = () => crypto.randomUUID();

export const createBlankCertificateDesign = (
	orientation: CertificateDesign["orientation"] = "portrait",
): CertificateDesign => ({
	version: 1,
	orientation,
	background: "#ffffff",
	elements: [],
});

export const createGuidedCertificateDesign = (): CertificateDesign => ({
	version: 1,
	orientation: "portrait",
	background: "#fffdf5",
	elements: [
		{ id: id(), type: "text", x: 150, y: 90, text: "ZERTIFIKAT", fill: "#172554", stroke: "#172554", strokeWidth: 0, fontSize: 38, fontFamily: "Times-Roman", rotation: 0, horizontalAlignment: "center" },
		{ id: id(), type: "text", x: 180, y: 160, text: "wird verliehen an", fill: "#475569", stroke: "#475569", strokeWidth: 0, fontSize: 18, fontFamily: "Helvetica", rotation: 0, horizontalAlignment: "center" },
		{ id: id(), type: "field", x: 150, y: 205, field: "participantName", fill: "#172554", stroke: "#172554", strokeWidth: 0, fontSize: 34, fontFamily: "Times-Roman", rotation: 0, horizontalAlignment: "center" },
		{ id: id(), type: "text", x: 180, y: 285, text: "für das erfolgreiche Bestehen von", fill: "#475569", stroke: "#475569", strokeWidth: 0, fontSize: 18, fontFamily: "Helvetica", rotation: 0, horizontalAlignment: "center" },
		{ id: id(), type: "field", x: 150, y: 325, field: "projectName", fill: "#172554", stroke: "#172554", strokeWidth: 0, fontSize: 26, fontFamily: "Times-Roman", rotation: 0, horizontalAlignment: "center" },
		{ id: id(), type: "field", x: 150, y: 405, field: "issuedAt", fill: "#475569", stroke: "#475569", strokeWidth: 0, fontSize: 14, fontFamily: "Helvetica", rotation: 0, horizontalAlignment: "center" },
		{ id: id(), type: "seal", x: 385, y: 410, width: 210, height: 75, fill: "#172554", stroke: "#b98b3b", strokeWidth: 1, fontSize: 11, fontFamily: "Helvetica", rotation: 0 },
		{ id: id(), type: "qr", x: 510, y: 490, width: 62, height: 62, fill: "#172554", stroke: "#172554", strokeWidth: 0, fontSize: 10, fontFamily: "Helvetica", rotation: 0 },
	],
});

export const formatCertificateField = (
	field: CertificateFieldKey,
	values: Record<CertificateFieldKey, string>,
) => values[field] ?? "";
